import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, Plus, Pencil, Heart } from "lucide-react";
import { toast } from "sonner";

// Method-based image fallback shared with Recipes page
const METHOD_IMG = {
  v60: "https://images.unsplash.com/photo-1545665225-b23b99e4d45e?auto=format&fit=crop&w=1200&q=80",
  chemex: "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=1200&q=80",
  espresso: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=1200&q=80",
  frenchpress: "https://images.unsplash.com/photo-1485808191679-5f86510681a2?auto=format&fit=crop&w=1200&q=80",
  aeropress: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80",
  moka: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=1200&q=80",
  coldbrew: "https://images.unsplash.com/photo-1517701604599-bb29b565090c?auto=format&fit=crop&w=1200&q=80",
  turkish: "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=1200&q=80",
};
const recipeImg = (r) => r?.image_url || METHOD_IMG[(r?.method || "").toLowerCase()] || null;

export default function MyRecipes() {
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  React.useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const { data: mine = [], isLoading: loadingMine } = useQuery({
    queryKey: ["my-recipes"],
    queryFn: endpoints.myRecipes,
    enabled: !!user,
  });

  const { data: favorites = [], isLoading: loadingFavs } = useQuery({
    queryKey: ["favoriteRecipes"],
    queryFn: endpoints.favoriteRecipes,
    enabled: !!user,
  });

  const onDelete = async (id) => {
    try {
      await endpoints.deleteRecipe(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["my-recipes"] });
      qc.invalidateQueries({ queryKey: ["recipes"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Could not delete");
    }
  };

  const onUnfav = async (id) => {
    try {
      await endpoints.toggleFavorite(id);
      qc.invalidateQueries({ queryKey: ["favoriteIds"] });
      qc.invalidateQueries({ queryKey: ["favoriteRecipes"] });
      toast.success(isRTL ? "أُزيلت من المفضلة" : "Removed from favorites");
    } catch {
      toast.error(isRTL ? "حدث خطأ" : "Something went wrong");
    }
  };

  if (loading || !user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="my-recipes-page">
      <div className="flex items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">{user.name}</div>
          <h1 className={`${serifClass} text-4xl lg:text-5xl text-[#2A2421]`}>{t.nav.mine}</h1>
        </div>
        <Button onClick={() => navigate("/recipes/new")} className="rounded-full bg-[#2A2421] text-[#F9F6F0]" data-testid="new-recipe-btn">
          <Plus className="w-4 h-4 me-2" /> {t.recipes.add}
        </Button>
      </div>

      <Tabs defaultValue="mine" className="w-full">
        <TabsList className="bg-[#F9F6F0] border border-[#2A2421]/10 rounded-full p-1 h-auto">
          <TabsTrigger
            value="mine"
            className="rounded-full px-5 py-2 data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0]"
            data-testid="tab-mine"
          >
            {isRTL ? "وصفاتي" : "My Recipes"} <span className="ms-1.5 text-xs opacity-70">({mine.length})</span>
          </TabsTrigger>
          <TabsTrigger
            value="favorites"
            className="rounded-full px-5 py-2 data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0]"
            data-testid="tab-favorites"
          >
            <Heart className="w-3.5 h-3.5 me-1.5" />
            {isRTL ? "المفضلة" : "Favorites"} <span className="ms-1.5 text-xs opacity-70">({favorites.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="mt-8">
          {loadingMine ? (
            <div className="text-[#5C544F]">{t.common.loading}</div>
          ) : mine.length === 0 ? (
            <div className="soft-card rounded-2xl p-10 text-center text-[#5C544F]" data-testid="my-recipes-empty">
              {t.recipes.empty}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6" data-testid="my-recipes-grid">
              {mine.map((r) => (
                <MineCard key={r.id} r={r} lang={lang} isRTL={isRTL} serifClass={serifClass} t={t} navigate={navigate} onDelete={onDelete} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites" className="mt-8">
          {loadingFavs ? (
            <div className="text-[#5C544F]">{t.common.loading}</div>
          ) : favorites.length === 0 ? (
            <div className="soft-card rounded-2xl p-10 text-center text-[#5C544F]" data-testid="favorites-empty">
              {isRTL
                ? "لم تضف أي وصفة للمفضلة بعد. اضغط على القلب على أي وصفة لإضافتها."
                : "You haven't added any favorites yet. Tap the heart on any recipe to save it."}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6" data-testid="favorites-grid">
              {favorites.map((r) => (
                <FavCard key={r.id} r={r} lang={lang} isRTL={isRTL} serifClass={serifClass} t={t} navigate={navigate} onUnfav={onUnfav} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const MineCard = ({ r, lang, isRTL, serifClass, t, navigate, onDelete }) => (
  <div className="soft-card rounded-2xl overflow-hidden" data-testid={`my-recipe-${r.id}`}>
    {recipeImg(r) && (
      <div className="aspect-[16/9]"><img src={recipeImg(r)} alt="" className="w-full h-full object-cover" /></div>
    )}
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">{r.method}</div>
      <div className={`${serifClass} text-2xl text-[#2A2421]`}>{lang === "ar" ? r.title_ar : r.title_en}</div>
      <div className="mt-4 flex items-center gap-2">
        <Button
          onClick={() => navigate(`/recipes/${r.id}/edit`)}
          variant="outline"
          size="sm"
          className="rounded-full"
          data-testid={`edit-${r.id}`}
        >
          <Pencil className="w-4 h-4 me-2" /> {isRTL ? "تعديل" : "Edit"}
        </Button>
        <Button
          onClick={() => {
            if (window.confirm(isRTL ? "تأكيد الحذف؟" : "Confirm delete?")) onDelete(r.id);
          }}
          variant="ghost"
          size="sm"
          className="text-red-600 hover:bg-red-50"
          data-testid={`delete-${r.id}`}
        >
          <Trash2 className="w-4 h-4 me-2" /> {t.recipes.delete}
        </Button>
      </div>
    </div>
  </div>
);

const FavCard = ({ r, lang, isRTL, serifClass, t, navigate, onUnfav }) => (
  <div className="soft-card rounded-2xl overflow-hidden" data-testid={`favorite-${r.id}`}>
    {recipeImg(r) && (
      <div className="aspect-[16/9]"><img src={recipeImg(r)} alt="" className="w-full h-full object-cover" /></div>
    )}
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">{r.method}</div>
      <div className={`${serifClass} text-2xl text-[#2A2421]`}>{lang === "ar" ? r.title_ar : r.title_en}</div>
      <div className="mt-2 text-xs text-[#5C544F]">
        {t.recipes.by} <span className="text-[#2A2421]">{r.user_name || (isRTL ? "وصفات الموقع" : "Brew Library")}</span>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button
          onClick={() => navigate("/recipes")}
          variant="outline"
          size="sm"
          className="rounded-full"
          data-testid={`open-recipe-${r.id}`}
        >
          {isRTL ? "افتح الوصفة" : "Open recipe"}
        </Button>
        <Button
          onClick={() => onUnfav(r.id)}
          variant="ghost"
          size="sm"
          className="text-[#C86240] hover:bg-[#C86240]/10"
          data-testid={`unfav-${r.id}`}
        >
          <Heart className="w-4 h-4 me-2 fill-[#C86240]" /> {isRTL ? "إزالة" : "Remove"}
        </Button>
      </div>
    </div>
  </div>
);
